/**
 * Recently Viewed Products WebComponent
 * Fetches and displays recently viewed products using server-rendered Liquid
 */
class RecentlyViewedProducts extends HTMLElement {
  // Shared cache across all instances to prevent duplicate loading
  static sharedCache = {
    isLoading: false,
    isLoaded: false,
    products: []
  };

  static sharedThemeSettings = null;

  // Migration flag to ensure it runs only once per session
  static migrationCompleted = false;

  // Selector constants for better maintainability
  static SELECTORS = {
    MAIN_SEARCH: '.main_search',
    TAB: '.predictive-search__result-tab',
    RECENTLY_VIEWED_TAB: '.predictive-search__result-tab--recently-viewed',
    ITEMS_LIST: ':scope > .search__items-list',
    RECENTLY_VIEWED_LIST: 'ul[data-type="recently-viewed"]'
  };

  static CLASSES = {
    ACTIVE: 'active',
    HIDDEN: 'hidden'
  };

  static ATTRIBUTES = {
    DATA_TYPE: 'data-type',
    TYPE_TARGET: 'typeTarget'
  };

  static CONFIG = {
    MAX_DISPLAY: 4,
    POLL_INTERVAL_MS: 50,
    LOAD_TIMEOUT_MS: 5000,
    PERCENTAGE_MULTIPLIER: 100,
    CURRENCY: 'USD',
    LOCALE: 'en-US',
    DEBUG: false // Set to true for development logging
  };

  static THEME_SETTINGS_DEFAULTS = {
    badgeIconColor: 'accent-2',
    badgeItalic: true,
    badgeHideSaleBadge: false,
    cardTextAlignment: 'center'
  };

  static HTML_CLASSES = {
    PRODUCT_LIST: 'search__items-list featured-products__products-list--horizontal predictive-search__results-list predictive-search__results-list_recently-viewed list-unstyled',
    PRODUCT_CARD: 'search__items-item collection-product-card collection-slider-card predictive-search__list-item quickview--hover',
    CARD_WRAPPER: 'card-wrapper js-color-swatches-wrapper cst_card_product bouncePop card-wrapper--recently-viewed',
    CARD: 'card card--product',
    CARD_INNER: 'card__inner full-unstyled-link',
    MEDIA: 'media media--transparent media--square media--hover-effect',
    BADGE_WRAP: 'badge-wrap',
    BADGE_METATEXT: 'badge badge-metatext',
    CARD_BADGE: 'card__badge card__badge--product-card',
    TICK_BOXES_LIST: 'cst_product_metafield_text cst_product_metafield_text_tags',
    TICK_BOX_ITEM: 'cst_meta_text cst_meta_text_tags'
  };

  static MERGEABLE_PROPERTIES = [
    'tickBoxes', 'okendoReviewCount', 'okendoStarRating',
    'productLabel'
  ];

  constructor() {
    super();
    this.storageKey = 'recently_viewed_products';
    this.maxDisplay = RecentlyViewedProducts.CONFIG.MAX_DISPLAY;
    this.products = [];
    this._tabClickHandler = null; // Track event handler for cleanup
    
    // Theme settings from data attributes (not cached)
    this.themeSettings = {
      badgeIconColor: null,
      badgeItalic: null,
      badgeHideSaleBadge: null,
      cardTextAlignment: null
    };
  }

  connectedCallback() {
    // Load theme settings from data attributes
    this.loadThemeSettings();
    
    // Check if this component is within the search context
    // If yes, pre-load products in background for instant display
    const isInSearch = this.closest(RecentlyViewedProducts.SELECTORS.MAIN_SEARCH);
    if (isInSearch) {
      this.preloadProducts();
    }
  }

  disconnectedCallback() {
    this.cleanupEventListeners();
  }

  cleanupEventListeners() {
    if (this._tabClickHandler) {
      const mainSearch = this.closest(RecentlyViewedProducts.SELECTORS.MAIN_SEARCH);
      if (mainSearch) {
        mainSearch.removeEventListener('click', this._tabClickHandler);
      }
      this._tabClickHandler = null;
    }
  }

  /**
   * Load current theme settings from data attributes
   * Uses shared settings across instances for better performance
   */
  loadThemeSettings() {
    if (RecentlyViewedProducts.sharedThemeSettings) {
      this.themeSettings = RecentlyViewedProducts.sharedThemeSettings;
      return;
    }
    const defaults = RecentlyViewedProducts.THEME_SETTINGS_DEFAULTS;
    
    this.themeSettings = {
      badgeIconColor: this.dataset.badgeIconColor || defaults.badgeIconColor,
      badgeItalic: this.dataset.badgeItalic !== 'false', // default true
      badgeHideSaleBadge: this.dataset.badgeHideSaleBadge === 'true', // default false
      cardTextAlignment: this.dataset.cardTextAlignment || defaults.cardTextAlignment
    };
    
    // Cache theme settings for other instances
    RecentlyViewedProducts.sharedThemeSettings = this.themeSettings;
    
    // Migrate old cached data (only once per session)
    this.migrateOldCachedData();
  }

  /**
   * Migration: Remove deprecated theme settings from cached product data
   * Runs only once per session for better performance
   */
  migrateOldCachedData() {
    // Skip if already migrated in this session
    if (RecentlyViewedProducts.migrationCompleted) return;
    
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) {
        RecentlyViewedProducts.migrationCompleted = true;
        return;
      }
      
      const products = JSON.parse(stored);
      let needsMigration = false;
      
      // Check if any product has deprecated theme settings
      const deprecatedProps = ['badgeIconColor', 'badgeItalic', 'badgeHideSaleBadge', 'cardTextAlignment'];
      
      const migratedProducts = products.map(product => {
        const cleanProduct = { ...product };
        
        deprecatedProps.forEach(prop => {
          if (cleanProduct.hasOwnProperty(prop)) {
            delete cleanProduct[prop];
            needsMigration = true;
          }
        });
        
        return cleanProduct;
      });
      
      // Only update storage if migration was needed
      if (needsMigration) {
        localStorage.setItem(this.storageKey, JSON.stringify(migratedProducts));
        if (RecentlyViewedProducts.CONFIG.DEBUG) {
          console.warn('[RecentlyViewedProducts] Migrated cached data to new format (removed theme settings)');
        }
      }
      RecentlyViewedProducts.migrationCompleted = true;
    } catch (error) {
      this.logError('Failed to migrate cached data', error);
      // Still mark as completed to avoid retry loops
      RecentlyViewedProducts.migrationCompleted = true;
    }
  }

  /**
   * Log errors in development mode for better debugging
   */
  logError(context, error) {
    if (RecentlyViewedProducts.CONFIG.DEBUG) {
      console.warn(`[RecentlyViewedProducts] ${context}:`, error);
    }
  }

  /**
   * Pre-loads products in the background without displaying them
   * This ensures instant display when search modal opens
   * Uses shared cache to prevent duplicate loading across desktop/mobile instances
   */
  async preloadProducts() {
    const cache = RecentlyViewedProducts.sharedCache;
    if (cache.isLoading || cache.isLoaded) return;
    cache.isLoading = true;
    
    try {
      const storedProducts = this.getStoredProducts();
      
      if (!storedProducts || storedProducts.length === 0) {
        cache.isLoaded = true;
        cache.isLoading = false;
        return;
      }

      const productsToDisplay = storedProducts.slice(0, this.maxDisplay);

      // Fetch product data using Shopify API and merge with stored data
      await this.fetchRenderedProducts(productsToDisplay, true); // true = preload mode
      
      cache.products = [...this.products];
      cache.isLoaded = true;
    } catch (error) {
      this.logError('Failed to preload products', error);
    } finally {
      cache.isLoading = false;
    }
  }

  /**
   * Helper method to render products from cache
   */
  renderCachedProducts(cache) {
    this.products = [...cache.products];
    if (this.products.length > 0) {
      this.renderProductsList();
    } else {
      this.renderEmpty();
    }
  }

  async loadProducts() {
    const cache = RecentlyViewedProducts.sharedCache;
    
    try {
      // If cache is already loaded, use it
      if (cache.isLoaded) {
        this.renderCachedProducts(cache);
        return;
      }

      // If cache is currently loading, wait for it
      if (cache.isLoading) {
        await this.waitForLoad();
        this.renderCachedProducts(cache);
        return;
      }

      // Fallback: load products if cache is not available
      const storedProducts = this.getStoredProducts();
      
      if (!storedProducts || storedProducts.length === 0) {
        this.renderEmpty();
        return;
      }

      const productsToDisplay = storedProducts.slice(0, this.maxDisplay);

      cache.isLoading = true;

      try {
        // Fetch product data using Shopify API and merge with stored data
        await this.fetchRenderedProducts(productsToDisplay, false); // false = not preload, render immediately
        
        cache.products = [...this.products];
        cache.isLoaded = true;
      } finally {
        cache.isLoading = false;
      }
    } catch (error) {
      this.logError('Failed to load products', error);
      this.renderEmpty();
    }
  }

  /**
   * Wait for loading to complete (checks shared cache)
   * Uses a flag to prevent double-resolve and properly cleanup timers
   */
  async waitForLoad() {
    const cache = RecentlyViewedProducts.sharedCache;
    
    return new Promise((resolve) => {
      let resolved = false;
      
      const finish = () => {
        if (resolved) return;
        resolved = true;
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve();
      };
      
      const checkInterval = setInterval(() => {
        if (!cache.isLoading) finish();
      }, RecentlyViewedProducts.CONFIG.POLL_INTERVAL_MS);

      const timeoutId = setTimeout(finish, RecentlyViewedProducts.CONFIG.LOAD_TIMEOUT_MS);
    });
  }

  getStoredProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      this.logError('Failed to read from localStorage', error);
      return [];
    }
  }

  /**
   * Merge stored metadata with fetched product data
   */
  mergeStoredData(product, storedProduct) {
    RecentlyViewedProducts.MERGEABLE_PROPERTIES.forEach(prop => {
      if (storedProduct[prop] !== undefined) {
        product[prop] = storedProduct[prop];
      }
    });
    return product;
  }

  async fetchRenderedProducts(storedProducts, isPreload = false) {
    try {
      // Fetch individual product data using Shopify's product JSON endpoint
      const productPromises = storedProducts.map(async (storedProduct) => {
        const product = await this.fetchProductData(storedProduct.handle);
        if (product) {
          // Merge stored data with fetched product data
          this.mergeStoredData(product, storedProduct);
        }
        return product;
      });
      
      const results = await Promise.allSettled(productPromises);
      
      // Filter successful fetches
      this.products = results
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => result.value);

      if (!isPreload) {
        if (this.products.length > 0) {
          this.renderProductsList();
        } else {
          this.renderEmpty();
        }
      }
    } catch (error) {
      this.logError('Failed to fetch rendered products', error);
      if (!isPreload) {
        this.renderEmpty();
      }
    }
  }

  async fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        this.logError(`Failed to fetch product: ${handle}`, new Error(`HTTP ${response.status}`));
        return null;
      }
      return await response.json();
    } catch (error) {
      this.logError(`Failed to fetch product data for: ${handle}`, error);
      return null;
    }
  }

  renderProductsList() {
    this.innerHTML = this.generateProductsHTML();
    this.initializeWidgets();
    this.showRecentlyViewedTab();
    this.setupTabSwitching();
    this.dispatchLoadedEvent();
  }

  generateProductsHTML() {
    return `
      <ul class="${RecentlyViewedProducts.HTML_CLASSES.PRODUCT_LIST}"
          id="predictive-search-results-recently-viewed-list"
          role="group"
          aria-labelledby="predictive-search-recently-viewed"
          data-type="recently-viewed">
        ${this.products.map((product, index) => this.renderProductCard(product, index)).join('')}
      </ul>
    `;
  }

  initializeWidgets() {
    this.initializeColorSwatches();
    this.initializeOkendoReviews();
  }

  initializeColorSwatches() {
    if (typeof colorSwatches === 'function') {
      try {
        colorSwatches();
      } catch (error) {
        this.logError('Failed to initialize color swatches', error);
      }
    }
  }

  initializeOkendoReviews() {
    if (typeof window.okeWidgetApi !== 'undefined' && window.okeWidgetApi) {
      try {
        if (typeof window.okeWidgetApi.initWidget === 'function') {
          window.okeWidgetApi.initWidget();
        } else if (typeof window.okeWidgetApi.refreshWidgets === 'function') {
          window.okeWidgetApi.refreshWidgets();
        }
      } catch (error) {
        this.logError('Failed to initialize Okendo reviews', error);
      }
    }
  }

  dispatchLoadedEvent() {
    this.dispatchEvent(new CustomEvent('recently-viewed-loaded', {
      detail: { hasProducts: true, count: this.products.length },
      bubbles: true
    }));
  }

  /**
   * Extract and format product data for rendering
   */
  extractProductData(product) {
    const featuredImage = product.featured_image || product.images[0];
    const secondaryImage = product.images && product.images.length > 1 ? product.images[1] : null;
    const variant = product.variants && product.variants[0] ? product.variants[0] : product;
    const price = variant.price || product.price;
    const compareAtPrice = variant.compare_at_price || product.compare_at_price;
    const available = product.available;
    const onSale = compareAtPrice && compareAtPrice > price;
    const priceVaries = product.price_varies;
    // Use current theme settings instead of cached product settings
    const textAlignment = this.themeSettings.cardTextAlignment;
    const variantId = product.variants && product.variants[0] ? product.variants[0].id : product.id;
    
    const formattedPrice = this.formatPrice(price);
    const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
    const moneyPrice = priceVaries ? `From ${formattedPrice}` : formattedPrice;

    return {
      featuredImage,
      secondaryImage,
      variant,
      price,
      compareAtPrice,
      available,
      onSale,
      priceVaries,
      textAlignment,
      variantId,
      formattedPrice,
      formattedComparePrice,
      moneyPrice
    };
  }

  /**
   * Generate badge HTML for product card
   */
  generateBadgeHtml(productData, product) {
    const { available, onSale, compareAtPrice, price } = productData;
    // Use current theme settings instead of cached product settings
    const badgeIconColor = this.themeSettings.badgeIconColor;
    const badgeItalic = this.themeSettings.badgeItalic;
    const hideSaleBadge = this.themeSettings.badgeHideSaleBadge && onSale && available;
    
    let badgeHtml = `<div class="${RecentlyViewedProducts.HTML_CLASSES.BADGE_WRAP}">`;
    
    // Show custom product label if available
    if (product.productLabel) {
      badgeHtml += `
        <div class="${RecentlyViewedProducts.HTML_CLASSES.BADGE_METATEXT}">
          ${this.escapeHtml(product.productLabel)}
        </div>
      `;
    }
    
    // Only show card__badge if not hiding sale badge
    if (!hideSaleBadge) {
      let cardBadgeClasses = RecentlyViewedProducts.HTML_CLASSES.CARD_BADGE;
      if (badgeItalic) cardBadgeClasses += ' card__badge_italic';
      if (onSale && available) cardBadgeClasses += ' on_sale';
      
      badgeHtml += `<div class="${cardBadgeClasses}">`;
      
      // Determine which badge to show
      if (!available && compareAtPrice > price) {
        badgeHtml += `
          <span class="badge badge-text badge--bottom-left color-${badgeIconColor}" aria-hidden="true">
            Sold out
          </span>
        `;
      } else if (compareAtPrice > price && available) {
        const discountPercent = Math.abs(Math.round(((price - compareAtPrice) / compareAtPrice) * RecentlyViewedProducts.CONFIG.PERCENTAGE_MULTIPLIER));
        badgeHtml += `
          <div class="badge badge-discount badge--bottom-left color-${badgeIconColor}" aria-hidden="true">
            ${discountPercent}<small>%</small>
          </div>
          <span class="badge badge-text badge--bottom-left color-${badgeIconColor}" aria-hidden="true">
            Sale
          </span>
        `;
      } else if (!available) {
        badgeHtml += `
          <span class="badge not-available-sold-out badge-text badge--bottom-left color-${badgeIconColor}" aria-hidden="true">
            Sold out
          </span>
        `;
      }
      
      badgeHtml += '</div>';
    }
    
    badgeHtml += '</div>';
    return badgeHtml;
  }

  /**
   * Generate tick boxes HTML
   */
  generateTickBoxesHtml(product) {
    if (!product.tickBoxes || !Array.isArray(product.tickBoxes) || product.tickBoxes.length === 0) {
      return '';
    }
    
    return `
      <ul class="${RecentlyViewedProducts.HTML_CLASSES.TICK_BOXES_LIST}">
        ${product.tickBoxes.map(tick => 
          `<li class="${RecentlyViewedProducts.HTML_CLASSES.TICK_BOX_ITEM}">${this.escapeHtml(tick)}</li>`
        ).join('')}
      </ul>
    `;
  }

  /**
   * Generate add to cart button HTML
   */
  generateAddToCartHtml(productData, product) {
    const { available, variantId } = productData;
    
    return `
      <product-form class="product-form" data-hide-errors="true">
        <form method="post" action="/cart/add" 
              id="product-form-recently-viewed-${product.id}" 
              accept-charset="UTF-8" 
              class="form" 
              enctype="multipart/form-data" 
              novalidate="novalidate" 
              data-type="add-to-cart-form">
          <input type="hidden" name="form_type" value="product" />
          <input type="hidden" name="utf8" value="✓" />
          <input type="hidden" name="id" value="${variantId}" ${!available ? 'disabled' : ''} />
          <button id="ProductSubmitButton-recently-viewed-${product.id}"
                  type="submit"
                  name="add"
                  class="product-form__submit button button--primary"
                  ${!available ? 'disabled' : ''}>
            <span>
              ${available ? 'ADD TO CART' : 'Sold out'}
            </span>
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </button>
        </form>
      </product-form>
    `;
  }

  /**
   * Generate product images HTML
   */
  generateProductImages(product, productData) {
    const { featuredImage, secondaryImage } = productData;
    const escapedTitle = this.escapeHtml(product.title);
    
    let imagesHtml = '';
    
    if (featuredImage) {
      imagesHtml += `
        <img srcset="${featuredImage}?width=165 165w,
                      ${featuredImage}?width=278 278w,
                      ${featuredImage}?width=360 360w,
                      ${featuredImage}?width=535 535w,
                      ${featuredImage}?width=720 720w"
             src="${featuredImage}?width=360"
             sizes="(min-width: 1200px) 360px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
             alt="${escapedTitle}"
             class="motion-reduce media--first"
             loading="lazy"
             width="360"
             height="360">
      `;
    } else {
      imagesHtml += `
        <div class="card__content">
          <div class="placeholder-svg">${this.getInitials(product.title)}</div>
        </div>
      `;
    }
    
    if (secondaryImage) {
      imagesHtml += `
        <img srcset="${secondaryImage}?width=165 165w,
                      ${secondaryImage}?width=278 278w,
                      ${secondaryImage}?width=360 360w,
                      ${secondaryImage}?width=535 535w,
                      ${secondaryImage}?width=720 720w"
             src="${secondaryImage}?width=360"
             alt="${escapedTitle}"
             class="motion-reduce media--first"
             loading="lazy"
             width="360"
             height="360">
      `;
    }
    
    return imagesHtml;
  }

  /**
   * Generate price HTML
   */
  generatePriceHtml(productData) {
    const { available, onSale, moneyPrice, formattedComparePrice } = productData;
    
    return `
      <div class="price ${!available ? 'price--sold-out' : ''} ${onSale ? 'price--on-sale' : ''} mini-price">
        <dl>
          <div class="price__regular custom_subscription_price">
            <dt>
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
            </dt>
            <dd>
              <span class="price-item price-item--regular">
                ${moneyPrice}
              </span>
            </dd>
          </div>
          <div class="price__sale">
            <dt>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
            </dt>
            <dt class="price__compare">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
            </dt>
            <dd>
              <span class="price-item price-item--sale">
                ${moneyPrice}
              </span>
            </dd>
            <dd class="price__compare">
              <span class="price-item price-item--regular">
                ${formattedComparePrice}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    `;
  }

  /**
   * Main method to render a product card
   */
  renderProductCard(product, index) {
    const productData = this.extractProductData(product);
    const badgeHtml = this.generateBadgeHtml(productData, product);
    const tickBoxesHtml = this.generateTickBoxesHtml(product);
    const addToCartHtml = this.generateAddToCartHtml(productData, product);
    const imagesHtml = this.generateProductImages(product, productData);
    const priceHtml = this.generatePriceHtml(productData);
    const escapedTitle = this.escapeHtml(product.title);
    const { textAlignment } = productData;
    
    // Assemble the complete product card HTML
    return `
      <li class="${RecentlyViewedProducts.HTML_CLASSES.PRODUCT_CARD}"
          id="predictive-search-option-recently-viewed-${index}"
          role="option"
          aria-selected="false">
        <div class="${RecentlyViewedProducts.HTML_CLASSES.CARD_WRAPPER}"
             data-product="${product.handle}" tabindex="0">
          <span class="visually-hidden">${escapedTitle}</span>
          <div class="${RecentlyViewedProducts.HTML_CLASSES.CARD} ${textAlignment ? `card--product--${textAlignment}` : ''}" tabindex="-1">
            <div class="${RecentlyViewedProducts.HTML_CLASSES.CARD_INNER}">
              <div class="${RecentlyViewedProducts.HTML_CLASSES.MEDIA}" style="padding-bottom: 100%;">
                ${imagesHtml}
              </div>
            </div>
            ${badgeHtml}
            <a href="${product.url}" 
               class="link link--overlay card-wrapper__link--overlay card-product__link js-color-swatches-link bouncePop" 
               aria-label="View Product ${escapedTitle}"></a>
          </div>
          <div class="card-information">
            <div class="card-information__wrapper card-information__wrapper--align-${textAlignment}">
              <div class="cst_product_rattings" data-product-id="${product.id}">
                ${product.okendoReviewCount && product.okendoStarRating ? `
                  <div data-oke-star-rating data-oke-reviews-product-id="shopify-${product.id}">
                    ${product.okendoStarRating}
                  </div>
                ` : '<!-- No reviews yet -->'}
              </div>
              <div class="card-title_info">
                <div class="card__title">
                  <a class="full-unstyled-link js-color-swatches-link" 
                     href="${product.url}" 
                     aria-label="link">
                    ${escapedTitle}
                  </a>
                </div>
                ${priceHtml}
              </div>
              ${tickBoxesHtml}
              ${addToCartHtml}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  formatPrice(cents) {
    return new Intl.NumberFormat(RecentlyViewedProducts.CONFIG.LOCALE, {
      style: 'currency',
      currency: RecentlyViewedProducts.CONFIG.CURRENCY
    }).format(cents / 100);
  }

  getInitials(title) {
    return title
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  renderEmpty() {
    this.innerHTML = '';
    
    // Hide the Recently Viewed tab
    this.hideRecentlyViewedTab();
    
    // Setup tab switching - this will activate the first available tab
    this.setupTabSwitching();
    
    // Dispatch empty event
    this.dispatchEvent(new CustomEvent('recently-viewed-empty', {
      detail: { hasProducts: false },
      bubbles: true
    }));
  }

  showRecentlyViewedTab() {
    const mainSearch = this.closest(RecentlyViewedProducts.SELECTORS.MAIN_SEARCH);
    if (!mainSearch) return;

    const recentlyViewedTab = mainSearch.querySelector(RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_TAB);
    if (recentlyViewedTab) {
      recentlyViewedTab.classList.remove(RecentlyViewedProducts.CLASSES.HIDDEN);
      recentlyViewedTab.style.display = '';
    }
  }

  hideRecentlyViewedTab() {
    const mainSearch = this.closest(RecentlyViewedProducts.SELECTORS.MAIN_SEARCH);
    if (!mainSearch) return;

    const recentlyViewedTab = mainSearch.querySelector(RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_TAB);
    if (recentlyViewedTab) {
      recentlyViewedTab.classList.add(RecentlyViewedProducts.CLASSES.HIDDEN);
      recentlyViewedTab.classList.remove(RecentlyViewedProducts.CLASSES.ACTIVE);
      recentlyViewedTab.style.display = 'none';
    }
  }

  setupTabSwitching() {
    const context = this.getTabContext();
    if (!context) return;
    
    this.activateDefaultTab(context);
    this.attachTabEventListeners(context);
  }

  getTabContext() {
    const mainSearch = this.closest(RecentlyViewedProducts.SELECTORS.MAIN_SEARCH);
    if (!mainSearch) return null;
    
    return {
      mainSearch,
      tabs: mainSearch.querySelectorAll(RecentlyViewedProducts.SELECTORS.TAB),
      lists: this.collectLists(mainSearch),
      recentlyViewedList: this.querySelector(RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_LIST),
      recentlyViewedTab: mainSearch.querySelector(RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_TAB)
    };
  }

  collectLists(mainSearch) {
    // Use spread operator for cleaner array creation
    const lists = [...mainSearch.querySelectorAll(RecentlyViewedProducts.SELECTORS.ITEMS_LIST)];
    
    const recentlyViewedList = this.querySelector(RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_LIST);
    if (recentlyViewedList) {
      lists.push(recentlyViewedList);
    }
    
    return lists;
  }

  activateDefaultTab(context) {
    // Clear all active states first
    this.clearActiveStates(context.tabs, context.lists);
    
    // Determine and activate the appropriate default tab
    const tabToActivate = this.determineDefaultTab(context);
    if (tabToActivate) {
      this.activateTab(tabToActivate.tab, tabToActivate.list);
    }
  }

  clearActiveStates(tabs, lists) {
    tabs.forEach(tab => tab.classList.remove(RecentlyViewedProducts.CLASSES.ACTIVE));
    lists.forEach(list => list.classList.remove(RecentlyViewedProducts.CLASSES.ACTIVE));
  }

  determineDefaultTab(context) {
    // Priority 1: Recently viewed if available
    if (context.recentlyViewedTab && context.recentlyViewedList) {
      return { tab: context.recentlyViewedTab, list: context.recentlyViewedList };
    }
    
    // Priority 2: First available non-hidden tab
    const firstTab = this.findFirstAvailableTab(context.tabs);
    if (!firstTab) return null;
    
    const correspondingList = context.lists.find(
      list => list.getAttribute(RecentlyViewedProducts.ATTRIBUTES.DATA_TYPE) === 
              firstTab.dataset[RecentlyViewedProducts.ATTRIBUTES.TYPE_TARGET]
    );
    
    return correspondingList ? { tab: firstTab, list: correspondingList } : null;
  }

  findFirstAvailableTab(tabs) {
    const recentlyViewedClass = RecentlyViewedProducts.SELECTORS.RECENTLY_VIEWED_TAB.replace('.', '');
    return Array.from(tabs).find(tab => 
      tab.style.display !== 'none' && 
      !tab.classList.contains(RecentlyViewedProducts.CLASSES.HIDDEN) &&
      !tab.classList.contains(recentlyViewedClass)
    );
  }

  activateTab(tab, list) {
    tab.classList.add(RecentlyViewedProducts.CLASSES.ACTIVE);
    list.classList.add(RecentlyViewedProducts.CLASSES.ACTIVE);
  }

  attachTabEventListeners(context) {
    // Clean up any existing listener
    this.cleanupEventListeners();
    
    // Use event delegation for better performance
    this._tabClickHandler = (event) => {
      const tab = event.target.closest(RecentlyViewedProducts.SELECTORS.TAB);
      if (!tab) return;
      
      event.preventDefault();
      this.handleTabClick(tab, context.tabs, context.lists);
    };
    
    context.mainSearch.addEventListener('click', this._tabClickHandler);
  }

  handleTabClick(clickedTab, allTabs, allLists) {
    const typeTarget = clickedTab.dataset[RecentlyViewedProducts.ATTRIBUTES.TYPE_TARGET];
    
    // Update tab active states
    allTabs.forEach(tab => tab.classList.remove(RecentlyViewedProducts.CLASSES.ACTIVE));
    clickedTab.classList.add(RecentlyViewedProducts.CLASSES.ACTIVE);
    
    // Update list active states
    allLists.forEach(list => {
      const listType = list.getAttribute(RecentlyViewedProducts.ATTRIBUTES.DATA_TYPE);
      list.classList.toggle(RecentlyViewedProducts.CLASSES.ACTIVE, listType === typeTarget);
    });
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
